import React from "react";
import "./LandingPage.css"

const features = [
  {
    title: "Phase 0 — Kp Index Predictor",
    points: [
      "Real-time planetary geomagnetic Kp index (0–9) from NOAA & DSCOVR data — critical for space weather awareness.",
      "Backend fetches and caches Kp data 2 hours.",
      "Offline support with timestamped local caching ensures continuity.",
    ],
  },
  {
    title: "Phase 1 — Solar System Core",
    points: [
      "Accurate heliocentric planetary positions sourced from JPL Horizons, now including the Moon and Pluto.",
      "Interactive 3D renderer with zoom, pan, reset, constraints, and responsive design.",
      "Planet cards feature animated temperature glows, authentic soundscapes, and detailed mission timelines.",
      "Realistic ring systems for Saturn, Uranus, Jupiter, Neptune with animations and annotations.",
      "Custom addressables system for efficient, on-demand texture loading.",
    ],
  },
  {
    title: "Phase 2 — Satellite Tracker & Spacecrafts",
    points: [
      "Handles 65,000+ satellites via R-tree spatial indexing and optimized data pipelines.",
      "Backend fetches, categorizes, and generates JSON snapshots.",
      "Frontend mirrors R-tree and uses InstancedMesh with clustering for smooth 3D rendering.",
      "Satellite positions are derived from TLEs using ECI conversion and aligned to J2000 frame.",
      "3D models of the ISS and Hubble, with smooth loading and a curated set of over 10,000 NASA images to explore.",
      "Robust caching and fallback mechanisms for seamless user experience.",
    ],
  },
  {
    title: "Phase 3 — Conjunction & Collision Risk Tracking",
    points: [
      "Live Conjunction Data Message (CDM) ingestion from Space-Track's public feed, merged and deduplicated against the satellite catalog.",
      "Dedicated 'Active Conjunctions' filter isolates every satellite currently involved in a tracked close-approach event with a single toggle.",
      "Per-satellite risk panel displays time of closest approach, miss distance, and probability of collision when published.",
      "Predictive path tracking layered on top of CDM data for at-risk satellites.",
      "Backend scheduled sync keeps conjunction data current alongside existing TLE and metadata refresh jobs.",
    ],
  },
];

const sarFeatures = {
  title: "SAR Maritime Surveillance Pipeline",
  points: [
    "Independent SAR ship-detection and maritime domain awareness platform built around Sentinel-1 imagery via Microsoft's Planetary Computer.",
    "CFAR ship detection with land masking and morphological filtering, tuned for open-ocean and coastal scenes.",
    "AIS correlation against Global Fishing Watch data to flag 'dark vessels' — detections with no matching AIS transponder signal.",
    "Loitering detection and EEZ boundary analysis to surface behavior patterns relevant to maritime enforcement.",
    "Multi-model confidence scoring (brightness, isolation, shape) validated against AIS ground truth — AIS-corroborated detections score meaningfully higher confidence than uncorroborated ones.",
    "Built on a pluggable 'sar_platform' architecture: an environment registry (maritime is the first environment) sitting on a shared core signal-processing module, so new detection domains can be added without touching the core.",
    "Streamlit-based operational UI with a grouped sidebar and live metrics dashboard.",
  ],
};

const backendFeatures = [
  "Automated cron jobs fetching and categorizing satellite TLE data, generating structured JSON outputs",
  "Construction of R-tree spatial index enabling rapid spatial queries",
  "Filtered NASA imagery and metadata accessible via dedicated API endpoints",
  "Fallback with JSON caching and 'last-updated' timestamps for reliability",
];

const uxPolish = [
  "Milky Way intro animation with user-controlled skip preference",
  "Unified loading screen showing asset and server progress",
  "Responsive planet cards with smooth animations, audio, and scroll snapping",
  "Efficient resource cleanup and asset disposal on navigation to maintain performance",
];

const techStack = [
  "React with Three.js (InstancedMesh, GLTF loader)",
  "Django backend with scheduled cron jobs and management commands",
  "Custom asset addressables system for efficient resource management",
  "R-tree spatial indexing for ultra-fast spatial queries",
  "Integrations with NASA APIs, JPL Horizons, Space-Track, NOAA",
  "Progressive loading and intelligent caching strategies",
  "Python SAR pipeline (Streamlit) with Sentinel-1 / Microsoft Planetary Computer and Global Fishing Watch integrations",
];

const technicalSummary = [
  "Tracks 65,000+ satellites live with advanced R-tree spatial indexing for smooth performance across devices.",
  "Features real-time Kp index prediction critical for space weather and satellite safety.",
  "Surfaces live satellite conjunction risk using Space-Track CDM data, with miss distance, collision probability, and predictive path tracking where available.",
  "Get hands-on with the ISS and Hubble's core modules, plus browse through 10,000+ stunning NASA images pulled directly from their API.",
  "3D visuals powered by React and Three.js using InstancedMesh for efficient rendering.",
  "Optimized with custom caching, custom addressables system, and scheduled backend cron jobs.",
  "Companion SAR maritime surveillance pipeline detects and validates ship activity from satellite radar imagery, extending the platform's space-to-sea data story.",
];

const optimizationEngineering = [
  "InstancedMesh rendering and spatial clustering reduce GPU draw calls for smooth visuals.",
  "Custom addressables system enables on-demand asset loading and disposal, conserving memory.",
  "R-tree spatial indexing supports rapid spatial queries with incremental frontend updates.",
  "Satellite data split into JSON snapshots cached locally for offline use and fast loading.",
  "Debounced instantiation suspensions during camera moves minimize computation overhead.",
  "High-precision J2000 coordinate calculations for accurate satellite positioning.",
];

const educationalExploration = [
  "Explore an interactive 3D solar system with accurate planetary positions and orbital data.",
  "Engage with authentic planetary soundtracks and animated temperature glows for immersive experience.",
  "Dive into detailed ring systems with interactive annotations and animated speech bubbles.",
  "Track thousands of satellites live with rich metadata and launch/orbit details.",
  "Learn from the Kp index predictor and space weather insights supporting aurora forecasts and satellite safety.",
  "See how satellite tracking connects to real-world maritime awareness through the SAR ship-detection pipeline.",
  "Designed for accessibility and responsiveness across devices for scientists, educators, and enthusiasts.",
];

function Section({ title, points }) {
  return (
    <section>
      <h2 className="landingpage-title">{title}</h2>
      <div className="feature-section">
        <ul>
          {points.map((point, i) => (
            <li key={i}>{point}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function XploreLanding() {
  return (
    <>
      <header className="landingpage-header" role="banner">
        <h1 tabIndex={0}>
          <img
            src="/xplore_logo.png"
            alt="XPLORE logo"
            style={{ maxWidth: "250px", height: "auto" }}
          />
        </h1>
        <p tabIndex={0}>
          Real-time, interactive solar system and satellite exploration platform with high-res NASA imagery, fast 3D rendering, and optimized data pipelines — now paired with a companion SAR maritime surveillance pipeline.
        </p>
        <button
          className="landingpage-btn-primary"
          onClick={() =>
            window.open("https://xplorespace.app", "_blank", "noopener,noreferrer")
          }
        >
          Explore the Platform
        </button>
        <section className="live-demo-section">
          <h2 className="landingpage-title">Live Demo & Video Showcase</h2>
          <div className="demo-placeholder">
            <iframe
              src="https://www.youtube.com/embed/KQK0jOZ8x4k"
              width="100%"
              height="338"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="XPLORE Video Demo"
            />
            <div className="video-link">
              Link:{' '}
              <a
                href="https://xplorespace.app"
                target="_blank"
                rel="noopener noreferrer"
              >
                xplorespace.app
              </a>
            </div>
          </div>
        </section>

      </header>

      <main className="landingpage-title-container">
        <Section title="Technical Summary" points={technicalSummary} />
        <Section title="Optimization & Engineering Techniques" points={optimizationEngineering} />
        <Section title="Educational & Exploration Value" points={educationalExploration} />

        {features.map((feature, idx) => (
          <Section key={idx} title={feature.title} points={feature.points} />
        ))}

        <Section title={sarFeatures.title} points={sarFeatures.points} />

        <Section title="Backend & Data Pipeline" points={backendFeatures} />
        <Section title="UX/UI Polish & Mobile Responsiveness" points={uxPolish} />

        <section>
          <h2 className="landingpage-title">Navigation & Interaction Preview</h2>
          <p className="interaction-tip" tabIndex={0}>
            Zoom, pan; click on any planet  {'->'} Swipe through the planetary cards to access information about rings, space missions, and satellites (if categories are applicable).
          </p>
          <p className="interaction-tip" tabIndex={1}>
            <strong>
              These screenshots are provided solely to help guide the user through the application.
            </strong>
          </p>
          <div className="navigation-preview">
            <img src="/images/landingpage/1.png" />
            <img src="/images/landingpage/2.png" />
            <img src="/images/landingpage/3.png" />
            <img src="/images/landingpage/4.png" />
            <img src="/images/landingpage/5.png" />
            <img src="/images/landingpage/6.png" />
            <img src="/images/landingpage/7.png" />
            <img src="/images/landingpage/8.png" />
            {/* <img src="/images/landingpage/9.png" /> */}
          </div>
        </section>

        <section>
          <h2 className="landingpage-title">Tech Stack & Data Sources</h2>
          <div className="feature-section tech-list">
            {techStack.map((tech, i) => (
              <span key={i}>{tech}</span>
            ))}
          </div>
        </section>

        <section className="interaction-tip">
           <h2 className="landingpage-title">WHAT'S NEXT? (Planned Features)</h2>

            {/* <div className="phase">
              <h3>Satellite–SAR Integration</h3>
              <ul>
                <li>Tie MDA (and other tracked) satellites into the SAR pipeline so ship-detection data is driven by the current/relative position of the imaging satellite over an area.</li>
                <li>Add a dedicated MDA satellite filter category to showcase currently deployed MDA satellites within the tracker.</li>
              </ul>
            </div> */}

            {/* <div className="phase">
              <h3>Environmental & Waste Data Layer</h3>
              <ul>
                <li>Click-to-country metrics: energy consumption, renewable vs. non-renewable mix, waste per capita, water pollution, air quality, industrial emissions, and ocean pollution.</li>
                <li>Filterable by country, continent, or global total, with a focus on highlighting ocean waste and where cleanup efforts should be concentrated.</li>
              </ul>
            </div> */}

            <div className="phase">
              <h3 className="landingpage-title-3">Mars Exploration Module </h3>
              <ul>
                <li><strong>Perseverance Rover</strong> integration in 3D visualization, with a Health Monitor for speed, location, and vital stats, plus AI-assisted safest-route calculations.</li>
                <li>Paused in favor of the satellite–SAR integration and environmental data layer above.</li>
              </ul>
            </div>
        </section>
      </main>
      <footer>
        <div className="footer-contact">
          <p>
            Contact:{" "}
            <a href="mailto:steve_pham18@hotmail.com">steve_pham18@hotmail.com</a>
          </p>
        </div>
        <p>
          © 2025 XPLORE project. Built with React, Three.js, Django, and NASA APIs.{" "}
          <a href="https://xplorespace.app" target="_blank" rel="noopener noreferrer">
            Live Site
          </a>
        </p>
      </footer>
    </>
  );
}
